import type { Language } from '../types';

function resolveLocale(language?: Language | string): string {
    if (!language) return 'en';
    if (language === 'he') return 'he-IL';
    if (language === 'ar') return 'ar';
    return language;
}

function isRtlLanguage(language?: Language | string): boolean {
    return language === 'he' || language === 'ar';
}

/** Prevent parent RTL/LTR context from reordering mixed date strings. */
function wrapBidiIsolate(text: string, rtl: boolean): string {
    return `${rtl ? '\u2067' : '\u2066'}${text}\u2069`;
}

function formatRtlWeekRange(start: Date, end: Date, locale: string): string {
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const sameYear = start.getFullYear() === end.getFullYear();
    const isStartCurrentYear = start.getFullYear() === new Date().getFullYear();
    const isEndCurrentYear = end.getFullYear() === new Date().getFullYear();

    if (sameMonth) {
        const monthYear = new Intl.DateTimeFormat(locale, { 
            month: 'long', 
            ...(isStartCurrentYear ? {} : { year: 'numeric' as const })
        }).format(start);
        return `${start.getDate()}–${end.getDate()} ${monthYear}`;
    }

    const startLabel = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        ...((sameYear && isStartCurrentYear) ? {} : { year: 'numeric' as const }),
    }).format(start);
    const endLabel = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        ...(isEndCurrentYear ? {} : { year: 'numeric' as const }),
    }).format(end);

    return `${startLabel} – ${endLabel}`;
}

export function formatWeekRange(start: Date, end: Date, language?: Language | string): string {
    const locale = resolveLocale(language);
    const rtl = isRtlLanguage(language);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const sameYear = start.getFullYear() === end.getFullYear();
    const isStartCurrentYear = start.getFullYear() === new Date().getFullYear();
    const isEndCurrentYear = end.getFullYear() === new Date().getFullYear();

    if (rtl) {
        return wrapBidiIsolate(formatRtlWeekRange(start, end, locale), true);
    }

    if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat.prototype.formatRange === 'function') {
        try {
            return wrapBidiIsolate(
                new Intl.DateTimeFormat(locale, {
                    day: 'numeric',
                    month: 'short',
                    ...((sameYear && isStartCurrentYear) ? {} : { year: 'numeric' as const }),
                }).formatRange(start, end),
                false,
            );
        } catch {
            // fall through
        }
    }

    if (sameMonth) {
        const monthYear = new Intl.DateTimeFormat(locale, { 
            month: 'long', 
            ...(isStartCurrentYear ? {} : { year: 'numeric' as const })
        }).format(start);
        return wrapBidiIsolate(`${start.getDate()}–${end.getDate()} ${monthYear}`, false);
    }

    const startLabel = start.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...((sameYear && isStartCurrentYear) ? {} : { year: 'numeric' as const }),
    });
    const endLabel = end.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        ...(isEndCurrentYear ? {} : { year: 'numeric' as const }),
    });
    return wrapBidiIsolate(`${startLabel} – ${endLabel}`, false);
}

export function weekRangeDir(language?: Language | string): 'rtl' | 'ltr' {
    return isRtlLanguage(language) ? 'rtl' : 'ltr';
}
